import { useState, useRef, useEffect } from 'react';
import { processResponseForTable } from '../utils/jsonTableProcessor';
import staticRequirements from '../assets/jira_requirements.json'; // Import static json with requirements.

/**
 * Custom hook for handling chat message submission and processing
 */
export const useMessageHandling = ({
  lastMessage,
  setLastMessage,
  initialInput,
  setInitialInput,
  isLoading,
  setIsLoading,
  attachedFiles,
  setAttachedFiles,
  keepFiles,
  formatFilesForPrompt,
  parsedFileData,
  config,
  prompts
}) => {
  const [inputValue, setInputValue] = useState('');
  const textareaRef = useRef(null);
  
  // Lambda function URL from config
  const LAMBDA_URL = config.API.LAMBDA_URL;

  // Auto-resize textarea as user types
  useEffect(() => {
    if (textareaRef.current) {
      if (!inputValue.trim()) {
        textareaRef.current.style.height = '24px';
      } else {
        // First reset height to get correct scrollHeight calculation
        textareaRef.current.style.height = '24px';
        // Then set to scrollHeight with maximum
        textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 150) + 'px';
      }
    }
  }, [inputValue, isLoading]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  // Extract Jira ticket number from input
  const extractJiraTickets = (input) => {
    // const ticketRegex = /^\#REQ-(\d+)/;
    // const match = input.match(ticketRegex);
    // return match ? `REQ-${match[1]}` : null;
    const ticketRegex = /(?:^|[,;|\s])(?:\#)?(?:REQ)-(\d+)/g;
  
    const tickets = [];
    let match;
  
    // Find all matches in the input string
    while ((match = ticketRegex.exec(input)) !== null) {
      tickets.push(`REQ-${match[1]}`);
    }
  
    return tickets.length > 0 ? tickets : null;
  };

  // Get data for specific Jira ticket from parsed file
  const getJiraTicketData = (ticketIds, parsedData) => {
    // Return null if no ticket IDs provided
    if (!ticketIds || !Array.isArray(ticketIds) || ticketIds.length === 0) {
      return null;
    }
  
    // Map each ticket ID to its data
    const results = ticketIds.map(ticketId => {
      let result = null;
    
      // First, look for the ticket in the static JSON data
      if (staticRequirements && staticRequirements[ticketId]) {
        console.log(`Found ticket ${ticketId} in static JSON data`);
        result = staticRequirements[ticketId];
      }
    
      // Look for the ticket in the parsed data
      if (parsedData && parsedData[ticketId]) {
        return result ? { ...result, ...parsedData[ticketId] } : parsedData[ticketId];
      }
    
      return result || null;
    });
  
    // Filter out null results (tickets not found)
    const validResults = results.filter(result => result !== null);
    
    // Return the array of ticket data or null if no data was found
    return validResults.length > 0 ? validResults : null;
  };

  // Check if input starts with "/prompt" command
  const isPromptCommand = (input) => {
    return /^\/prompt\s*:/i.test(input);
  };

  // Remove "/prompt:" prefix if present
  const removePromptPrefix = (input) => {
    return input.replace(/^\/prompt\s*:/i, '').trim();
  };
  
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!inputValue.trim()) return;

    // Store the current input
    let currentInput = inputValue;
    setInputValue('');
    setIsLoading(true);
    
    try {
      let processedInput;
      const isPromptCmd = isPromptCommand(currentInput);
       // Check for Jira ticket in the message
      const ticketsId = extractJiraTickets(currentInput);
      
      // If this is a /prompt command, clear the response window first
      if (isPromptCmd && lastMessage) {
        setLastMessage(null);
      }

      // Format attached files content for prompt context
      const adaptedFiles = attachedFiles.map(file => ({
        fileName: file.fileName,
        fileType: file.fileType,
        content: file.fileContent
      }));
      // Pure file context for prompt in case if no tickets found, but files attached
      const filesContext = formatFilesForPrompt(adaptedFiles);
      
      // If this is a /prompt command or the first input (no lastMessage), treat as initial requirements
      if (isPromptCmd || !lastMessage || ticketsId) {
        // Remove the /prompt prefix if present
        const cleanedInput = isPromptCmd ? removePromptPrefix(currentInput) : currentInput;
        
        // Save as initial input for future use
        setInitialInput(cleanedInput);
        
        let specificData = null;
        
        // parsed data is data from last file(done only for MVP)
        if (ticketsId) {
          // Get specific ticket data from parsed file if available
          specificData = getJiraTicketData(ticketsId, parsedFileData);
        }
        
        // Inject the input into the test case prompt template
        processedInput = prompts.TEST_CASE.replace('{jira_issues}', cleanedInput);
        if (filesContext && !specificData) {
          console.log("WARNING: Full file context will be send")
          processedInput = processedInput.replace('{knowledge_base}', filesContext);
        } else if (specificData) {
          processedInput = processedInput.replace('{knowledge_base}', ""); // NO CONTENT
          processedInput += processedInput.replace('{specific_ticket_data}', JSON.stringify(specificData, null, 2));
        }
      } else {
        // This is a feedback input - check for Jira ticket reference
        const ticketsId = extractJiraTickets(currentInput);
        let specificData = null;
        
        if (ticketsId && parsedFileData) {
          // Get specific ticket data from parsed file if available
          specificData = getJiraTicketData(ticketsId, parsedFileData);
        }
        
        // Combine previous output with current feedback and specific ticket data if found
        // Use the original content (not the processed HTML) from lastMessage
        processedInput = `Previous output: ${lastMessage.originalContent || lastMessage.content}\n\nUser feedback: ${currentInput}`;
        
        if (specificData) {
          processedInput += `\n\nSpecific ticket data: ${JSON.stringify(specificData, null, 2)}`;
        }
        
        // Also include the original initial input for context
        processedInput += `\n\nOriginal test requirements: ${initialInput}`;
      }

      // Send the request to Lambda
      const response = await fetch(LAMBDA_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Origin': window.location.origin
        },
        body: JSON.stringify({ 
          query: processedInput,
          model_id: config.MODELS.DEFAULT
        }),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      const data = await response.json();
      // const data = {
      //   response: {
      //     content: [
      //       {
      //         text: processedInput
      //       }
      //     ]
      //   },
      //   // Optional additional fields (could be present depending on your API)
      //   status: 'success',
      //   metadata: {
      //     model_id: 'grok-3',
      //     timestamp: '2025-03-17T12:34:56Z',
      //     request_id: 'abc123xyz'
      //   }
      // };
      
      // Extract assistant message from the response
      const assistantContent = data.response.content && data.response.content[0] && 
                              data.response.content[0].text ? 
                              data.response.content[0].text : 
                              'Sorry, I could not process the response.';
      
      // Process the response to create HTML table version
      const processedContent = processResponseForTable(assistantContent);
      // Store both the original content and the processed HTML content
      setLastMessage({ 
        role: 'assistant', 
        content: processedContent,     // HTML formatted content for display
        originalContent: assistantContent  // Original unformatted content for future requests
      });
    } catch (error) {
      console.error('Error calling Lambda:', error);
      // Set error message
      setLastMessage({ 
        role: 'assistant', 
        content: 'Sorry, I encountered an error processing your request. Technical details: ' + error.message,
        originalContent: 'Sorry, I encountered an error processing your request. Technical details: ' + error.message
      });
    } finally {
      setIsLoading(false);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      
      // Clear attached files if not set to keep them
      if (!keepFiles) {
        setAttachedFiles([]);
      }
    }
  };

  return {
    inputValue,
    setInputValue,
    handleInputChange,
    handleSubmit,
    textareaRef
  };
};